import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, 
  CheckSquare, 
  TrendingUp, 
  AlertCircle,
  Clock,
  DollarSign,
  Loader2,
  Receipt
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { clientsAPI, tasksAPI, invoicesAPI } from '../services/api';
import apiClient from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    pendingTasks: 0,
    completedThisMonth: 0,
    revenue: { thisMonth: 0, lastMonth: 0, growth: 0 },
    invoiced: { thisMonth: 0, lastMonth: 0 },
    upcomingDeadlines: 0,
    overdueItems: 0
  });
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [totalReceivable, setTotalReceivable] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [statsRes, tasksRes, clientsRes, receivableRes] = await Promise.all([
        apiClient.get('/dashboard/stats'),
        tasksAPI.getAll(),
        clientsAPI.getAll({ sort_by: 'activity' }),
        invoicesAPI.getAccountsReceivable()
      ]);

      // Set stats
      setStats(statsRes.data);
      
      // Set total receivable
      setTotalReceivable(receivableRes.data.totalReceivable || 0);

      // Get upcoming tasks (not completed, sorted by due date)
      const tasks = tasksRes.data.tasks || [];
      const upcoming = tasks
        .filter(task => task.status !== 'completed')
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 5);
      setUpcomingTasks(upcoming);

      // Get recent clients (sorted by last activity)
      const clients = clientsRes.data.clients || [];
      const recent = clients
        .sort((a, b) => new Date(b.lastActivity || b.createdAt) - new Date(a.lastActivity || a.createdAt))
        .slice(0, 5);
      setRecentClients(recent);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: stats.totalClients,
      change: `${stats.activeClients} active`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Pending Tasks',
      value: stats.pendingTasks,
      change: `${stats.completedThisMonth} completed this month`,
      icon: CheckSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Invoiced This Month',
      value: `$${(stats.invoiced?.thisMonth || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: 'Based on invoice date',
      icon: Receipt,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: 'Invoiced Last Month',
      value: `$${(stats.invoiced?.lastMonth || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: 'Based on invoice date',
      icon: Receipt,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: 'Collected This Month',
      value: `$${(stats.revenue?.thisMonth || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: 'Based on payment date',
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Collected Last Month',
      value: `$${(stats.revenue?.lastMonth || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: 'Based on payment date',
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Total Receivable',
      value: `$${totalReceivable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: 'Outstanding invoices',
      icon: TrendingUp,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    },
    {
      title: 'Overdue Items',
      value: stats.overdueItems,
      change: stats.overdueItems > 0 ? 'Needs attention' : 'All on track',
      icon: Clock,
      color: stats.overdueItems > 0 ? 'text-red-600' : 'text-orange-600',
      bgColor: stats.overdueItems > 0 ? 'bg-red-50' : 'bg-orange-50'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.change}</p>
                </div>
                <div className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                Upcoming Tasks
              </span>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => navigate('/tasks')}
              >
                View All
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingTasks.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No pending tasks</p>
            ) : (
              <div className="space-y-4">
                {upcomingTasks.map((task) => (
                  <div 
                    key={task._id} 
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate('/tasks')}
                    data-testid={`task-${task._id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{task.title}</h4>
                        <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                          {task.priority}
                        </Badge>
                        {isOverdue(task.dueDate) && (
                          <Badge variant="destructive" className="text-xs">Overdue</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{task.client}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600">
                          Due {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Client Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Recent Client Activity
              </span>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => navigate('/clients')}
              >
                View All
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentClients.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No clients yet</p>
            ) : (
              <div className="space-y-4">
                {recentClients.map((client) => (
                  <div 
                    key={client._id} 
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/clients/${client._id}`)}
                    data-testid={`client-${client._id}`}
                  >
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold">
                      {client.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{client.name}</h4>
                        <Badge className={`text-xs ${getStatusColor(client.status)}`}>
                          {client.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {client.lastActivity 
                          ? `Last activity: ${format(new Date(client.lastActivity), 'MMM dd, h:mm a')}`
                          : `Added: ${format(new Date(client.createdAt), 'MMM dd, yyyy')}`
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-900">{client.openTasks || 0} tasks</p>
                      <p className="text-xs text-gray-500">{client.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {stats.overdueItems > 0 && (
        <Card className="border-orange-200 bg-orange-50" data-testid="overdue-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-900">
                  You have {stats.overdueItems} overdue item{stats.overdueItems > 1 ? 's' : ''}
                </h4>
                <p className="text-xs text-orange-700 mt-0.5">
                  Please review and update these tasks to stay on track
                </p>
              </div>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-orange-100 border-orange-300 text-orange-700"
                onClick={() => navigate('/tasks')}
              >
                View Tasks
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
